import { Dispatch, SetStateAction } from 'react';
import {
  UpsertFormRequest,
  UpsertScreenRequest,
  EntityDetailType,
  EntityTypeCode,
  ActionType,
  EntityDetailConfig,
  FormDto,
} from '@/apiClient';
import { UniqueIdentifier } from '@dnd-kit/core';

export type OptionType = {
  id?: string;
  category?: string | null;
  disabled?: boolean;
  sortOrder?: number;
  isArchived?: boolean;
  data?: string;
  label: string;
  value: string | number;
};

export type TBuilderFieldColumn = {
  id: string;
  items: IEntityDetail[];
};

export type TFormBuilderContext = {
  rootItem: TBuilderFieldColumn;
  setRootItem: Dispatch<SetStateAction<TBuilderFieldColumn>>;
  formContainers: TSection[];
  setFormContainers: Dispatch<SetStateAction<TSection[]>>;
  formBasicInfo: FormDto;
  setFormBasicInfo: Dispatch<SetStateAction<FormDto>>;
  swapSections: (oldIndex: number, newIndex: number) => void;
};

export const SORTABLE_TARGET_TYPE = {
  CONTAINER: 'container',
  ITEM: 'item',
};

// SECTION FOR ENTITY INTERFACE

type TConfigValidation = {
  required?: boolean;
  min?: number | null;
  max?: number | null;
};

type TEntityDetailConfig = {
  custom?: EntityDetailConfig['custom'];
  defaultValue?: string | null;
  detailDescription?: string;
  detailName?: string;
  entityDetailConfigEntityId?: string;
  entityDetailConfigId?: number;
  entityDetailId?: number;
  entityDetailUnitOfMeasurement?: number;
  entityDetailUnitOfMeasureScale?: number;
  isDefault?: boolean;
  isEnabled?: boolean;
  isDatabaseSearchEnable?: boolean;
  labelText?: string;
  options: { value: string; label: string }[];
  order?: number;
  organisationId?: number;
  placeholder?: string;
  validation?: TConfigValidation;
};

export interface IEntityDetail {
  entityDetaTEntityDetailValueilId?: number;
  entityDetailEntityId?: string;
  entityDetailId?: number;
  entityDetailName?: string;
  entityDetailDescription?: string;
  entityDetailValues?: any; // Temporary mark as any
  entityDetailType: EntityDetailType;
  entityTypeCode?: EntityTypeCode;
  entityDetailConfigs: TEntityDetailConfig;
}

export type TSortableItemtype = 'container' | 'item';

export type TSection = {
  title: string;
  id: UniqueIdentifier;
  items: Record<string, IEntityDetail[]>;
};

type ActionTypeOptionMap = Record<ActionType, OptionType>;

export const ACTION_TYPE_OPTIONS: OptionType[] = [
  { label: 'Create', value: ActionType.Create },
  { label: 'Edit', value: ActionType.Edit },
  { label: 'Delete', value: ActionType.Delete },
  { label: 'DBSearch', value: ActionType.Studbook },
  { label: 'Quick Search', value: ActionType.QuickSearch },
];

export const ACTION_TYPE_OPTIONS_MAP: ActionTypeOptionMap =
  ACTION_TYPE_OPTIONS.reduce(
    (acc, option) => ({ ...acc, [option.value]: option }),
    {} as ActionTypeOptionMap,
  );

type TFormConfig = Omit<UpsertFormRequest, 'actionType'> & {
  actionType: ActionType;
};

export type TCreateScreenPayload = Omit<
  UpsertScreenRequest,
  'type' | 'forms'
> & {
  type: EntityTypeCode;
  forms: Array<TFormConfig>;
};

export const ENTITY_DETAIL_NAME = {
  ADDRESS: 'Address',
  ANIMAL_STATUS: 'Animal Status',
  ANIMAL_NAME: 'Animal Name',
  ANIMAL_IDENT: 'Animal Ident',
  ANIMAL_TYPE: 'Animal Type',
  SIRE: 'Sire',
  DAM: 'Dam',
  BREEDER: 'Breeder',
  OWNER: 'Owner',
  ORIGINAL_OWNER: 'Original Owner',
  BREED_PERCENTS: 'BreedPercents',
  DATE_OF_BIRTH: 'Date of Birth',
  REGISTRATION_DATE: 'Registration Date',
  IMPORT_DATE: 'Import Date',
  AI_DATE: 'AI Date',
  DESEXED_DATE: 'Desexed Date',
  CLASSIFIED_DATE: 'Classified Date',
  INSPECTION_DATE: 'Inspection Date',
  RECIPIENT_DAM_BIRTH_DATE: 'Recipient Dam Birth Date',
  REGO_HEIGHT_DATE: 'Rego Height Date',
  TRANSFER_HEIGHT_DATE: 'Transfer Height Date',
  HEIGHT_AT_24MO_DATE: 'Height at 24mo Date',
  HEIGHT_AT_48MO_DATE: 'Height at 48mo Date',
  BY_AI: 'By AI',
  BY_ET: 'By ET',
  SEX: 'Sex',
  TATTOO: 'Tattoo',
  DISPOSAL_RECORD: 'Disposal Record',
  ALTERNATE_IDENTS: 'Alternate Idents',
  FIRST_NAME: 'First Name',
  LAST_NAME: 'Last Name',
  EMAIL: 'Email',
  MEMBER_IDENT: 'Member Ident',
  MEMBER_TYPE: 'Member Type',
  NAME: 'Name',
  PHONE: 'Phone',
  MEMBERSHIP: 'Membership',
  DATE_JOINED: 'Date Joined',
  DATE_RESIGNED: 'Date Resigned',
  MEMBER_STATUS: 'Member Status',
  BIRTH_WEIGHT: 'Birth Weight',
  SEX_CODE: 'Sex Code',
  COLOUR: 'Colour',
  NATIONAL_ID: 'National ID',
} as const;

export const ENTITY_DETAIL_ID = {
  TEMPLATE_DOCUMENT: 10155,
  TEMPLATE_NAME: 10154,
  TEMPLATE_USED_BY: 10153,
};

export const SYSTEM_REQUIRED_ENTITY_DETAILS = new Set<string>([
  ENTITY_DETAIL_NAME.ANIMAL_TYPE,
]);
